/* Source: https://github.com/KyberNetwork/kyberswap-interface/blob/860030939a688248772755ad15c2392f1d563e7a/src/components/TradingViewChart/datafeed.tsx */

import { useRef } from 'react';

import { configurationData } from '@/config/chart';
import { constructSymbolInfo, convertResolutionToSeconds, fetchHistory } from '@/helpers/datafeedHelper';
import { RootState, store } from '@/state/store';
import {
    ErrorCallback,
    HistoryCallback,
    IDatafeedChartApi,
    IExternalDatafeed,
    LibrarySymbolInfo,
    OnReadyCallback,
    PeriodParams,
    ResolutionString,
    ResolveCallback,
    SearchSymbolsCallback,
    SubscribeBarsCallback
} from '@/types/charting_library';
import { getUniversalInstrument } from './useInstrument';

export const useSimpleDatafeed = (id, chartId, symbol: string) => {
    const intervalRef = useRef<any>(null);

    return {
        onReady: (callback: OnReadyCallback) => {
            setTimeout(() => callback(configurationData));
        },
        resolveSymbol: async (
            symbolName: string,
            onSymbolResolvedCallback: ResolveCallback,
            onResolveErrorCallback: ErrorCallback
        ) => {
            setTimeout(() => {
                const state: RootState = store.getState();
                const pairs = state.marketPair.pairs;

                const TVWidget = window[chartId]?.TVWidget;

                if (TVWidget) {
                    TVWidget.chart()
                        .onIntervalChanged()
                        .subscribe(null, (resolution: ResolutionString) => {});
                }

                const pairToUse = symbolName;
                // const pairToUse = chartInstrument?.netdania !== symbolName ? chartInstrument?.netdania : symbolName;
                let symbolItem;
                if (pairs.length > 0) {
                    const allSymbols = pairs.map((pair) => {
                        const instrument = getUniversalInstrument(pair.celer);
                        return {
                            symbol: pair.netdania,
                            full_name: instrument?.histPriceSymbol || ':',
                            description: pair.show,
                            exchange: '',
                            type: instrument?.type || '',
                            instrument
                        };
                    });
                    symbolItem = allSymbols.find(({ symbol }) => symbol === pairToUse);
                } else {
                    const instrument = getUniversalInstrument('BTC/USD');
                    symbolItem = {
                        symbol: 'BTC/USD',
                        full_name: instrument?.histPriceSymbol || ':',
                        description: 'BTC/USD',
                        exchange: '',
                        type: instrument?.type || '',
                        instrument
                    };
                }
                if (symbolItem) {
                    const symbolInfo = constructSymbolInfo(symbolItem, configurationData) as any;
                    onSymbolResolvedCallback(symbolInfo);
                } else {
                    onResolveErrorCallback('Cannot resolve symbol');
                }
            }, 0);
        },
        getBars: async (
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            periodParams: PeriodParams,
            onHistory: HistoryCallback,
            onError: ErrorCallback
        ) => {
            if (window[chartId]?.chartLoaded) {
                const intervalSec = convertResolutionToSeconds(resolution);
                let { from, to } = periodParams;
                const firstDataRequest = periodParams.firstDataRequest;

                // ---- 1️⃣ Clamp initial load aggressively
                if (firstDataRequest) {
                    const INITIAL_VISIBLE_BARS = 350;
                    from = Math.max(from, to - INITIAL_VISIBLE_BARS * intervalSec);
                }

                // ---- 2️⃣ Align to candle boundaries
                from = align(from, intervalSec);
                to = align(to, intervalSec);

                const [provider, symbol] = symbolInfo.full_name.split(':');

                if (provider) {
                    fetchHistory(
                        '****',
                        symbol,
                        intervalSec,
                        { ...periodParams, from, to, firstDataRequest },
                        provider,
                        onHistory,
                        onError
                    );
                } else {
                    onHistory([], { noData: true });
                }
            } else {
                onHistory([], { noData: true });
            }
        },
        searchSymbols: (userInput: string, exchange: string, symbolType: string, onResult: SearchSymbolsCallback) => {
            const state: RootState = store.getState();
            const pairs = state.marketPair.pairs;

            const temp = userInput.replace('/', '').toUpperCase();
            const filtered: any = pairs
                .filter((pair) => pair.show.toUpperCase().replace('/', '').includes(temp))
                .map((pair) => pair.show);
            // const filtered = allSymbols.filter((symbol) => symbol.match(temp));
            const returned = filtered.map((symbol) => ({
                description: symbol,
                exchange: '',
                full_name: symbol,
                symbol: symbol,
                ticker: symbol,
                type: getUniversalInstrument(symbol)?.type || ''
            }));
            onResult(returned);
        },
        subscribeBars: async (
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            onTick: SubscribeBarsCallback
        ) => {
            if (intervalRef.current) clearInterval(intervalRef.current);

            const interval = setInterval(async () => {
                const state: RootState = store.getState();
                const updateBar = state.chartData.priceTicks[symbolInfo.name];
                if (updateBar && updateBar?.symbol === symbolInfo.name) {
                    const { symbol, ...bar } = updateBar;
                    onTick(bar);
                }
            }, 1000);

            intervalRef.current = interval;
        },
        unsubscribeBars: (listenerGuid: string) => {}
    } as IDatafeedChartApi & IExternalDatafeed;
};

function align(timeSec: number, resolutionSec: number): number {
    return Math.floor(timeSec / resolutionSec) * resolutionSec;
}
